import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionVideo from "@/components/MissionVideo";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import ChoiceCard from "@/components/ChoiceCard";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_CUSTOMER_OPTIONS,
  JR_HELP_KINDS,
} from "@/utils/jrDemoState";
import { getMissionVideo } from "@/utils/jrMissionMedia";

/** Map a saved customer string back to a card key (for restore on reload). */
function findCustomerKey(saved) {
  if (!saved) return { key: "", custom: "" };
  const match = JR_CUSTOMER_OPTIONS.find(
    (o) => o.key !== "custom" && o.label.toLowerCase() === saved.toLowerCase()
  );
  if (match) return { key: match.key, custom: "" };
  return { key: "custom", custom: saved };
}

/** Map a saved help label back to its key so reopening Mission 4 restores
 *  the selected card. (We persist the LABEL for plan display, not the key.) */
function findHelpKey(savedLabel) {
  if (!savedLabel) return "";
  const match = JR_HELP_KINDS.find(
    (h) => h.label.toLowerCase() === savedLabel.toLowerCase()
  );
  return match ? match.key : "";
}

export default function Mission4() {
  const navigate = useNavigate();
  const initial = loadJrState();
  const init = findCustomerKey(initial.customer);
  const [whoKey, setWhoKey] = useState(init.key);
  const [whoCustom, setWhoCustom] = useState(init.custom);
  const [helpKey, setHelpKey] = useState(findHelpKey(initial.helpKind));
  const [showOverlay, setShowOverlay] = useState(false);
  const video = getMissionVideo(4);

  const isCustomWho = whoKey === "custom";
  const whoOk = !!whoKey && (!isCustomWho || whoCustom.trim().length >= 2);
  const helpOk = !!helpKey;
  const canContinue = whoOk && helpOk;

  const onContinue = () => {
    if (!canContinue) return;
    const opt = JR_CUSTOMER_OPTIONS.find((o) => o.key === whoKey);
    const customer = isCustomWho ? whoCustom.trim() : opt ? opt.label : "";
    const helpKind = JR_HELP_KINDS.find((h) => h.key === helpKey);
    saveJrState({
      customer,
      helpKind: helpKind ? helpKind.label : "",
    });
    // 2 stars when the kid wrote their own customer.
    markMissionComplete(4, { customized: isCustomWho });
    setShowOverlay(true);
  };

  return (
    <ElementaryMissionPage
      missionNumber={4}
      title="Create Your Big Idea"
      lesson="Every business helps someone. A Junior CEO picks WHO they help and HOW."
      canContinue={canContinue}
      onContinue={onContinue}
      continueLabel="Save & Back to CEO City"
      testId="elementary-mission-4-page"
    >
      {video && (
        <MissionVideo url={video.url} title={video.title} testId="mission-4-video" />
      )}

      <div className="mb-6">
        <LemonBotJr
          greeting="Junior CEOs help real people. Pick who you'll help and what kind of help you'll give!"
          prompts={[
            "Step 1: pick WHO your business helps.",
            "Step 2: pick HOW you'll help them.",
          ]}
          compact
        />
      </div>

      {/* Step 1: WHO */}
      <section className="mb-8">
        <p className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
          Who will your business help?
        </p>
        <p className="text-sm text-gray-500 mb-4">Step 1 of 2 · Tap one</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {JR_CUSTOMER_OPTIONS.map((opt) => (
            <ChoiceCard
              key={opt.key}
              icon={opt.icon}
              label={opt.label}
              color={opt.color}
              selected={whoKey === opt.key}
              onSelect={() => setWhoKey(opt.key)}
              testId={`mission-4-who-${opt.key}`}
            />
          ))}
        </div>
        {isCustomWho && (
          <div className="mt-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Who is your customer?
            </label>
            <input
              type="text"
              value={whoCustom}
              onChange={(e) => setWhoCustom(e.target.value)}
              maxLength={100}
              placeholder="My customer is…"
              className="w-full px-4 py-3 text-lg border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
              data-testid="mission-4-who-custom-input"
              autoFocus
            />
          </div>
        )}
      </section>

      {/* Step 2: HOW */}
      <section className="mb-2">
        <p className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
          What kind of help will you give?
        </p>
        <p className="text-sm text-gray-500 mb-4">Step 2 of 2 · Tap one</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {JR_HELP_KINDS.map((h) => (
            <ChoiceCard
              key={h.key}
              icon={h.icon}
              label={h.label}
              color={
                ["yellow", "orange", "rose", "sky", "emerald", "amber"][
                  JR_HELP_KINDS.indexOf(h) % 6
                ]
              }
              selected={helpKey === h.key}
              onSelect={() => setHelpKey(h.key)}
              testId={`mission-4-help-${h.key}`}
            />
          ))}
        </div>
      </section>

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={4}
          missionTitle="Create Your Big Idea"
          message="Awesome! You named your customer and your kind of help. A real Junior CEO move."
          continueLabel="Back to CEO City"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
